import { Node, mergeAttributes } from '@tiptap/core';
import type MarkdownIt from 'markdown-it';
import type StateInline from 'markdown-it/lib/rules_inline/state_inline';
import type Token from 'markdown-it/lib/token';

type MarkdownItWithVideo = MarkdownIt & { __videoPluginApplied?: boolean };

const VIDEO_SYNTAX = /^@\[(?:video)\]\(([^)]+)\)/i;

const addVideoMarkdownPlugin = (markdownit: MarkdownIt) => {
  const md = markdownit as MarkdownItWithVideo;
  if (md.__videoPluginApplied) {
    return;
  }

  const videoRule = (state: StateInline, silent: boolean) => {
    const start = state.pos;
    if (state.src.charCodeAt(start) !== 0x40) {
      return false;
    }
    if (start > 0 && state.src[start - 1] === '\\') {
      return false;
    }

    const match = state.src.slice(start).match(VIDEO_SYNTAX);
    if (!match) {
      return false;
    }

    const src = match[1].trim();
    if (!src) {
      return false;
    }

    if (!silent) {
      const token = state.push('video', '', 0);
      token.block = true;
      token.attrs = [['src', src]];
    }

    state.pos += match[0].length;
    return true;
  };

  md.inline.ruler.before('link', 'video', videoRule);
  md.renderer.rules.video = (tokens: Token[], idx: number) => {
    const src = tokens[idx].attrGet('src');
    if (!src) {
      return '';
    }
    const escaped = md.utils.escapeHtml(src);
    return `<video src="${escaped}" controls="true" class="editor-video"></video>`;
  };

  md.__videoPluginApplied = true;
};

export interface VideoOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    video: {
      /**
       * Add a video
       */
      setVideo: (options: { src: string }) => ReturnType;
    };
  }
}

export const Video = Node.create<VideoOptions>({
  name: 'video',

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute('src'),
        renderHTML: (attributes) => {
          if (!attributes.src) {
            return {};
          }
          return {
            src: attributes.src,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'video[src]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'video',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        controls: 'true',
        class: 'editor-video',
      }),
    ];
  },

  addCommands() {
    return {
      setVideo:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  // Custom markdown serialization
  // In markdown, we'll use: @[video](url)
  addStorage() {
    return {
      markdown: {
        serialize(state: { text: (text: string, escape?: boolean) => void }, node: { attrs: { src: string } }) {
          state.text(`@[video](${node.attrs.src})`, false);
        },
        parse: {
          setup(markdownit: MarkdownIt) {
            addVideoMarkdownPlugin(markdownit);
          },
        },
      },
    };
  },
});
